import { downloadReadOnlyAttachment } from '../../../util/readOnlyAttachmentActions';
import { decodeDisplayText } from '../../../util/displayText';
import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from '@apollo/client';
import { prettySize } from '../../../util/fileSizeCalculator';
import { FiDownload, FiExternalLink, FiMoreVertical, FiX } from 'react-icons/fi';

import FileIcon from '../../../appearance/FileIcon';
import ImageIcon from '../../../appearance/ImageIcon';

import { imageExists } from '../../../util/imageExists';

import styled from 'styled-components';

import {
    NOTE_BACKGROUND,
    PRIMARY_COLOR_4,
    PRIMARY_COLOR_8,
    PALE_WHITE_4,
    PALE_WHITE_6,
    WHITE
} from '../../../appearance/Colors';

import CarouselModal from '../../../components/Modals/CarouselModal';

import useDownloadFile from '../../../hooks/useDownloadFile';
import { QUERY } from '../../../gql-operations';
import { LAYOUT, isMobileViewport, calculateImageFrameBounds } from '../layoutBreakpoints';
import { isFileTypeImage } from '../../../constants/fileUtils';
import { isTauriEnvironment, openExternalUrl } from '../../../utils/tauri';

const isPdfDocument = (mediaType = '', mediaFormat = '', mediaMime = '') => {
    const normalizedType = String(mediaType || '').toLowerCase();
    const normalizedFormat = String(mediaFormat || '').toLowerCase();
    const normalizedMime = String(mediaMime || '').toLowerCase();

    return (
        normalizedMime === 'application/pdf' ||
        normalizedType === 'application/pdf' ||
        (normalizedType === 'application' && normalizedFormat === 'pdf')
    );
};

const getThumbnailUrl = (thumbnail) => {
    if (!thumbnail) return null;
    if (typeof thumbnail === 'string') return thumbnail;
    if (typeof thumbnail !== 'object') return null;

    return (
        thumbnail.original ||
        thumbnail.lg ||
        thumbnail.md ||
        thumbnail.sm ||
        thumbnail.compressed?.original ||
        thumbnail.compressed?.lg ||
        thumbnail.compressed?.md ||
        thumbnail.compressed?.sm ||
        null
    );
};

const toStableUrlKey = value => {
    const raw = String(value || '').trim();
    if (!raw) return '';

    try {
        const parsed = new URL(raw);
        return `${parsed.origin}${parsed.pathname}`;
    } catch (error) {
        return raw.split('?')[0].split('#')[0];
    }
};

function FileSection({
    readOnly = false,
    file,
    isUploading = false,
    uploadProgress = 0,
    uploadStatus = 'uploading',
    onCancelUpload,
    messageId,
    isStarred = false,
    hideActions = false,
    forceStackPreviewLayout = false,
    showWorkspaceButton = false,
    onOpenVideoWorkspace
}) {
    const {t: lnT} = useTranslation();

    const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [pdfThumbnailFailed, setPdfThumbnailFailed] = useState(false);
    const [pdfThumbnailUrl, setPdfThumbnailUrl] = useState(null);
    const [showFileActionMenu, setShowFileActionMenu] = useState(false);
    const [fileActionMenuPosition, setFileActionMenuPosition] = useState({ top: 0, left: 0 });
    const wrapperRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const fileActionButtonRef = useRef(null);
    const fileActionMenuRef = useRef(null);
    const lastMeasuredWidthRef = useRef(0);
    const latestResolvedPdfThumbnailRef = useRef(null);
    const failedPdfThumbnailUrlsRef = useRef(new Set());
    const { url, name, displayName, size, type, format, cdn_url } = file;
    // Prefer CDN URL when available, fallback to signed S3 URL
    const fileUrl = cdn_url || url;
    const fallbackUrl = cdn_url ? url : null;
    const isPdfFile = isPdfDocument(type, format, file?.mime);
    const resolvedPdfThumbnailUrl = isPdfFile ? getThumbnailUrl(file?.thumbnail) : null;
    let maxTypeChar = 28;

    const downloadFile = useDownloadFile();
    const { refetch: refetchDownloadFile } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });

    useEffect(() => {
        if (!showFileActionMenu) {
            return undefined;
        }

        const updateFileActionMenuPosition = () => {
            const rect = fileActionButtonRef.current?.getBoundingClientRect();
            if (!rect) {
                return;
            }

            const menuWidth = 154;
            const horizontalPadding = 8;
            const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
            const left = Math.max(
                horizontalPadding,
                Math.min(rect.right - menuWidth, viewportWidth - menuWidth - horizontalPadding)
            );

            setFileActionMenuPosition({
                top: Math.round(rect.bottom + 6),
                left: Math.round(left)
            });
        };

        const handlePointerDown = event => {
            const target = event.target;
            if (fileActionMenuRef.current?.contains(target) || fileActionButtonRef.current?.contains(target)) {
                return;
            }

            setShowFileActionMenu(false);
        };

        const handleEscape = event => {
            if (event.key === 'Escape') {
                setShowFileActionMenu(false);
            }
        };

        updateFileActionMenuPosition();

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleEscape, true);
        window.addEventListener('resize', updateFileActionMenuPosition);
        window.addEventListener('scroll', updateFileActionMenuPosition, true);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape, true);
            window.removeEventListener('resize', updateFileActionMenuPosition);
            window.removeEventListener('scroll', updateFileActionMenuPosition, true);
        };
    }, [showFileActionMenu]);

    useEffect(() => {
        latestResolvedPdfThumbnailRef.current = resolvedPdfThumbnailUrl || null;
    }, [resolvedPdfThumbnailUrl]);

    useEffect(() => {
        if (!isPdfFile) {
            setPdfThumbnailUrl(null);
            setPdfThumbnailFailed(false);
            failedPdfThumbnailUrlsRef.current.clear();
            return;
        }

        const current = String(pdfThumbnailUrl || '').trim();
        const next = String(resolvedPdfThumbnailUrl || '').trim();
        const currentKey = toStableUrlKey(current);
        const nextKey = toStableUrlKey(next);

        // First mount / no active URL.
        if (!current && next) {
            setPdfThumbnailUrl(next);
            setPdfThumbnailFailed(false);
            return;
        }

        // Underlying image changed (different path), reset failure memory and swap.
        if (next && nextKey && nextKey !== currentKey) {
            failedPdfThumbnailUrlsRef.current.clear();
            setPdfThumbnailFailed(false);
            setPdfThumbnailUrl(next);
            return;
        }

        // No thumbnail available anymore.
        if (!next) {
            setPdfThumbnailUrl(null);
            setPdfThumbnailFailed(false);
            failedPdfThumbnailUrlsRef.current.clear();
        }
    }, [isPdfFile, resolvedPdfThumbnailUrl]);

    // Setup ResizeObserver
    useEffect(() => {
        let rafId = null;
        let isObserving = true;
        const STACK_LAYOUT_HYSTERESIS_PX = 20;
        
        const checkWidth = (observedWidth) => {
            if (!isObserving) return;
            if (wrapperRef.current) {
                const measuredWidth =
                    typeof observedWidth === 'number' && Number.isFinite(observedWidth)
                        ? observedWidth
                        : wrapperRef.current.getBoundingClientRect().width;
                const width = Number.isFinite(measuredWidth) ? measuredWidth : 0;
                lastMeasuredWidthRef.current = width;

                setIsSmallScreen(prevIsSmallScreen => {
                    if (!Number.isFinite(width) || width <= 0) {
                        return prevIsSmallScreen;
                    }

                    const stackThreshold = LAYOUT.PREVIEW_STACK_MAX;
                    const compactEnterThreshold = stackThreshold - STACK_LAYOUT_HYSTERESIS_PX;
                    const compactExitThreshold = stackThreshold + STACK_LAYOUT_HYSTERESIS_PX;

                    if (prevIsSmallScreen) {
                        return width <= compactExitThreshold;
                    }

                    if (width >= compactEnterThreshold) {
                        return false;
                    }

                    return true;
                });
            }
        };

        const throttledCheckWidth = observedWidth => {
            if (rafId || !isObserving) return;
            
            rafId = window.requestAnimationFrame(() => {
                checkWidth(observedWidth);
                rafId = null;
            });
        };

        resizeObserverRef.current = new ResizeObserver(entries => {
            const entry = Array.isArray(entries) && entries.length > 0 ? entries[0] : null;
            const entryWidth = Number(entry?.contentRect?.width || 0);
            throttledCheckWidth(entryWidth);
        });
        
        if (wrapperRef.current) {
            resizeObserverRef.current.observe(wrapperRef.current);
            checkWidth();
        }

        // Listen for drag start/end
        const handleDragStart = (e) => {
            // Don't interfere with audio scrubber
            if (e.target?.tagName === 'INPUT' && e.target?.type === 'range' && e.target?.hasAttribute('data-prevent-message-interaction')) {
                return;
            }
            isObserving = false;
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };

        const handleDragEnd = (e) => {
            // Don't interfere with audio scrubber
            if (e.target?.tagName === 'INPUT' && e.target?.type === 'range' && e.target?.hasAttribute('data-prevent-message-interaction')) {
                return;
            }
            isObserving = true;
            if (resizeObserverRef.current && wrapperRef.current) {
                resizeObserverRef.current.observe(wrapperRef.current);
                checkWidth(lastMeasuredWidthRef.current || undefined);
            }
        };

        document.addEventListener('dragstart', handleDragStart);
        document.addEventListener('dragend', handleDragEnd);

        return () => {
            isObserving = false;
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
            document.removeEventListener('dragstart', handleDragStart);
            document.removeEventListener('dragend', handleDragEnd);
        };
    }, []);

    const getImageWidth = () => {
        if (!wrapperRef.current) return LAYOUT.DEFAULT_MEDIA_WIDTH;
        
        const containerWidth = wrapperRef.current.getBoundingClientRect().width;
        let maxWidth = LAYOUT.DEFAULT_MEDIA_WIDTH;
        
        // Only resize if container is too small
        if (isSmallScreen && containerWidth < maxWidth + 32) {
            maxWidth = containerWidth - 16;
        }
        
        return maxWidth;
    };

    const renderCarousel = item => {
        return (
            <CarouselModal
                isOpen={lightboxIsOpen}
                closeModal={e => {
                    e.stopPropagation();
                    setLightboxIsOpen(false);
                }}
                items={[item]}
                fileType="File"
                messageId={messageId}
            />
        );
    };

    const fileName = () => {
        let title;
        if (displayName) {
            title = displayName;
        } else if (name) {
            title = name;
        } else if (url) {
            // in case that we haven't saved the name of the file
            title = url.substr(url.lastIndexOf('/') + 1);
        } else {
            title = lnT('messages:file');
        }
        return decodeDisplayText(title);
    };

    const getFileAspectRatio = () => {
        if (file?.width && file?.height && file.width > 0 && file.height > 0) {
            return file.height / file.width;
        }
        if (file?.aspect_ratio && typeof file.aspect_ratio === 'number' && !isNaN(file.aspect_ratio)) {
            return file.aspect_ratio;
        }
        return null;
    };

    const renderFileType = (fileUrlToRender, type, format, fixedPreviewWidth = null, isCompactPreview = false) => {
        const normalizedType = type || '';
        const normalizedFormat = format || '';
        const isPdf = isPdfDocument(normalizedType, normalizedFormat, file?.mime);
        // If no URL yet (uploading), show appropriate icon
        if (!fileUrlToRender) {
            if (normalizedType.includes('image')) {
                return (
                    <div>
                        <ImageIcon />
                    </div>
                );
            }
            return (
                <div>
                    <FileIcon
                        type={normalizedType}
                        format={normalizedFormat}
                        mime={file?.mime}
                        name={fileName()}
                    />
                </div>
            );
        }
        
        if (normalizedType.includes('video')) {
            const aspectRatio = getFileAspectRatio();
            const maxWidthPx = isSmallScreen ? null : 200;
            const maxWidth = isSmallScreen ? '100%' : '200px';
            const useAspectRatioLayout = Boolean(aspectRatio);
            const handleOpenWorkspace = event => {
                if (typeof onOpenVideoWorkspace !== 'function') {
                    return;
                }

                event.stopPropagation();
                onOpenVideoWorkspace({
                    src: fileUrlToRender,
                    originalUrl: fallbackUrl || fileUrlToRender || ''
                });
            };

            return (
                <div
                    data-message-media-interactive="true"
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: maxWidth,
                        borderRadius: 8,
                        overflow: 'hidden',
                        backgroundColor: '#000',
                        ...(useAspectRatioLayout ? (
                            maxWidthPx ? {
                                height: Math.round(maxWidthPx * aspectRatio),
                            } : {
                                paddingBottom: `${aspectRatio * 100}%`,
                                height: 0
                            }
                        ) : {})
                    }}
                >
                    {showWorkspaceButton && typeof onOpenVideoWorkspace === 'function' ? (
                        <button
                            type="button"
                            onClick={handleOpenWorkspace}
                            aria-label={lnT('messages:openWorkspace')}
                            title={lnT('messages:openInWorkspace')}
                            style={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 2,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                border: 0,
                                borderRadius: 999,
                                padding: '8px 10px',
                                cursor: 'pointer',
                                color: '#fff',
                                background: 'rgba(0, 0, 0, 0.72)',
                                backdropFilter: 'blur(8px)'
                            }}
                        >
                            <FiExternalLink size={14} />
                            <span>{lnT('messages:workspace')}</span>
                        </button>
                    ) : null}
                    <video
                        src={fileUrlToRender}
                        controls
                        preload="metadata"
                        playsInline
                        onClick={e => e.stopPropagation()}
                        onError={(e) => {
                            if (fallbackUrl && e.target.src !== fallbackUrl) {
                                e.target.src = fallbackUrl;
                            }
                        }}
                        style={{
                            position: useAspectRatioLayout ? 'absolute' : 'relative',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: useAspectRatioLayout ? '100%' : 'auto',
                            borderRadius: 8,
                            display: 'block'
                        }}
                    />
                </div>
            );
        } else if (normalizedType.includes('image')) {
            if (!normalizedType.includes('image/heic') || (normalizedFormat && !normalizedFormat.includes('heic'))) {
                const aspectRatio = getFileAspectRatio();
                const imageBounds = calculateImageFrameBounds(file, fixedPreviewWidth, isSmallScreen);
                const frameWidth = imageBounds.width;
                const frameHeight = imageBounds.height;
                const useAspectRatioLayout = Boolean(aspectRatio || frameHeight);

                return (
                    <ImageContainer
                        $isStarred={isStarred}
                        $fixedWidth={fixedPreviewWidth}
                        data-message-media-interactive="true"
                        isSmallScreen={isSmallScreen}
                        onClick={e => {
                            e.stopPropagation();
                            setLightboxIsOpen(true);
                        }}
                        style={{
                            position: 'relative',
                            width: '100%',
                            maxWidth: frameWidth ? `${frameWidth}px` : '100%',
                            borderRadius: 8,
                            ...(frameHeight ? {
                                height: `${frameHeight}px`,
                                overflow: 'hidden'
                            } : (aspectRatio ? {
                                paddingBottom: `${aspectRatio * 100}%`,
                                height: 0,
                                overflow: 'hidden'
                            } : {
                                minHeight: 140,
                                overflow: 'hidden'
                            }))
                        }}
                        >
                            <img
                                src={fileUrlToRender}
                                style={{ 
                                    position: (frameHeight || aspectRatio) ? 'absolute' : 'relative',
                                    top: (frameHeight || aspectRatio) ? 0 : 'auto',
                                    left: (frameHeight || aspectRatio) ? 0 : 'auto',
                                    width: '100%',
                                    height: (frameHeight || aspectRatio) ? '100%' : 'auto',
                                    objectFit: 'contain',
                                    borderRadius: 8,
                                    cursor: 'zoom-in'
                                }}
                            onError={(e) => {
                                // Fallback to S3 URL if CDN URL fails
                                if (fallbackUrl && e.target.src !== fallbackUrl) {
                                    e.target.src = fallbackUrl;
                                } else {
                                    e.target.style.display = 'none';
                                }
                            }}
                        />
                    </ImageContainer>
                );
            } else {
                return (
                    <div>
                        <ImageIcon />
                    </div>
                );
            }
        } else if (isPdf && pdfThumbnailUrl && !pdfThumbnailFailed) {
            return (
                <ImageContainer
                    $isStarred={isStarred}
                    $fixedWidth={fixedPreviewWidth}
                    data-message-media-interactive="true"
                    isSmallScreen={isCompactPreview}
                    onClick={e => {
                        e.stopPropagation();
                        setLightboxIsOpen(true);
                    }}
                >
                    <img
                        src={pdfThumbnailUrl}
                        style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: 8,
                            cursor: 'zoom-in'
                        }}
                        onError={() => {
                            const currentUrl = String(pdfThumbnailUrl || '').trim();
                            if (currentUrl) {
                                failedPdfThumbnailUrlsRef.current.add(currentUrl);
                            }

                            const latestResolved = String(latestResolvedPdfThumbnailRef.current || '').trim();
                            const canTryLatestResolved =
                                latestResolved &&
                                latestResolved !== currentUrl &&
                                !failedPdfThumbnailUrlsRef.current.has(latestResolved);

                            if (canTryLatestResolved) {
                                setPdfThumbnailUrl(latestResolved);
                                return;
                            }

                            setPdfThumbnailFailed(true);
                        }}
                        alt={lnT('messages:previewOf', { value1: String(fileName()) })}
                    />
                </ImageContainer>
            );
        } else {
            return (
                <div>
                    <FileIcon
                        type={normalizedType}
                        format={normalizedFormat}
                        mime={file?.mime}
                        name={fileName()}
                    />
                </div>
            );
        }
    };

    const hasPdfThumbnail = isPdfFile && Boolean(pdfThumbnailUrl) && !pdfThumbnailFailed;
    const isVideoPreview = Boolean(fileUrl && type?.includes('video'));
    const isImageFile = Boolean(
        fileUrl &&
        (isFileTypeImage(type || '') || (type?.includes('image') && (!type.includes('image/heic') || (format && !format.includes('heic')))))
    );
    const isImagePreview = isImageFile || hasPdfThumbnail || isVideoPreview;
    const shouldStackPdfPreview = hasPdfThumbnail && (forceStackPreviewLayout || isMobileViewport());
    const useStackedPreviewLayout = hasPdfThumbnail
        ? shouldStackPdfPreview
        : (isImageFile || (isSmallScreen && isImagePreview));
    const pdfPreviewFixedWidth = !useStackedPreviewLayout && hasPdfThumbnail ? 220 : null;
    const metaColor = isStarred ? PRIMARY_COLOR_4 : PALE_WHITE_4;
    const canShowFileActions = Boolean(fileUrl && !hideActions && !isUploading);

    const resolveFreshOpenUrl = async () => {
        if (readOnly || !file?.name) {
            return fileUrl || url;
        }

        try {
            const { data, error } = await refetchDownloadFile({
                data: {
                    fileName: file.name
                }
            });

            if (error) {
                throw error;
            }

            if (data?.downloadFile?.__typename === 'S3File' && (data.downloadFile.cdn_url || data.downloadFile.url)) {
                return data.downloadFile.cdn_url || data.downloadFile.url;
            }
        } catch (error) {
            console.error('Failed to refresh file URL before opening:', error);
        }

        return fileUrl || url;
    };

    const openFile = async event => {
        event?.stopPropagation?.();
        if (!fileUrl) {
            return;
        }

        const openUrl = await resolveFreshOpenUrl();

        if (isTauriEnvironment()) {
            openExternalUrl(openUrl);
        } else {
            const openedWindow = window.open('about:blank', '_blank');
            if (openedWindow) {
                openedWindow.opener = null;
                openedWindow.location.replace(openUrl);
            } else {
                window.open(openUrl, '_blank', 'noopener,noreferrer');
            }
        }

        setShowFileActionMenu(false);
    };

    const downloadCurrentFile = async event => {
        event?.stopPropagation?.();
        if (readOnly) {
            try { await downloadReadOnlyAttachment(file); } catch { return; }
        } else {
            downloadFile(file.name, file.size, file.displayName);
        }
        setShowFileActionMenu(false);
    };

    return (
        <FileWrapper ref={wrapperRef} isSmallScreen={isSmallScreen} isImagePreview={isImagePreview}>
            <FileContent
                isSmallScreen={isSmallScreen}
                isImagePreview={isImagePreview}
                useStackedPreviewLayout={useStackedPreviewLayout}
                $hasActions={canShowFileActions}
            >
                <div style={{ 
                    width: useStackedPreviewLayout
                        ? '100%'
                        : (pdfPreviewFixedWidth ? `${pdfPreviewFixedWidth}px` : 'auto'),
                    minWidth: useStackedPreviewLayout
                        ? 0
                        : (pdfPreviewFixedWidth ? `${pdfPreviewFixedWidth}px` : 0),
                    marginRight: useStackedPreviewLayout ? 0 : (isImagePreview ? 8 : 12),
                    flexShrink: 0
                }}>
                    {renderFileType(fileUrl, type, format, pdfPreviewFixedWidth, useStackedPreviewLayout)}
                </div>

                <InfoSection useStackedPreviewLayout={useStackedPreviewLayout}>
                    {fileUrl && !isUploading ? (
                        <FileNameLink
                            href={fileUrl || url}
                            target="_blank"
                            rel="noopener noreferrer"
                            $isStarred={isStarred}
                            title={fileName()}
                            data-message-interactive="true"
                            onClick={event => {
                                event.preventDefault();
                                openFile(event);
                            }}
                        >
                            {fileName()}
                        </FileNameLink>
                    ) : (
                        <FileNameText
                            $isStarred={isStarred}
                            title={fileName()}
                        >
                            {fileName()}
                        </FileNameText>
                    )}

                    <FileMetaRow>
                        {size ? (
                            <FileMetaText $isStarred={isStarred}>{prettySize(size)}</FileMetaText>
                        ) : null}

                        {isUploading ? (
                            <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: 8 }}>
                                <div style={{ 
                                    flex: 1,
                                    maxWidth: 100,
                                    height: 4, 
                                    backgroundColor: PALE_WHITE_6, 
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        height: '100%',
                                        width: `${Math.min(uploadProgress, 100)}%`,
                                        backgroundColor: uploadStatus === 'error' ? '#ff4444' : PRIMARY_COLOR_8,
                                        borderRadius: 2,
                                        transition: 'width 0.15s ease-out'
                                    }} />
                                </div>
                                <span style={{ fontSize: 12, color: metaColor }}>
                                    {uploadStatus === 'error' ? lnT('messages:failed') :
                                     uploadStatus === 'processing' ? lnT('common:saving') :
                                    size >= 2 * 1024 * 1024 * 1024 ? lnT('common:text', { value1: String(Number(uploadProgress || 0).toFixed(1)) }) : lnT('common:text', { value1: String(Math.round(Number(uploadProgress || 0))) })}
                                </span>
                                {typeof onCancelUpload === 'function' && uploadStatus !== 'error' ? (
                                    <button
                                        type="button"
                                        title={lnT('messages:cancelUpload')}
                                        aria-label={lnT('messages:cancelUpload')}
                                        onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onCancelUpload();
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: metaColor,
                                            borderRadius: 4,
                                            opacity: 0.7,
                                            transition: 'opacity 0.15s ease, color 0.15s ease'
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.opacity = '1';
                                            e.currentTarget.style.color = '#ff4444';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.opacity = '0.7';
                                            e.currentTarget.style.color = metaColor;
                                        }}
                                    >
                                        <FiX size={14} />
                                    </button>
                                ) : null}
                            </div>
                        ) : null}
                    </FileMetaRow>
                </InfoSection>
            </FileContent>
            {canShowFileActions ? (
                <FileActionsAnchor $useStackedPreviewLayout={useStackedPreviewLayout}>
                    <FileActionsButton
                        $isStarred={isStarred}
                        $isOverlay={useStackedPreviewLayout}
                        $isMenuOpen={showFileActionMenu}
                        ref={fileActionButtonRef}
                        type="button"
                        aria-label={lnT('messages:fileActions')}
                        aria-expanded={showFileActionMenu}
                        data-message-interactive="true"
                        onClick={event => {
                            event.stopPropagation();
                            setShowFileActionMenu(previous => !previous);
                        }}
                    >
                        <FiMoreVertical size={16} />
                    </FileActionsButton>
                    {showFileActionMenu && typeof document !== 'undefined'
                        ? ReactDOM.createPortal(
                              <FileActionMenu
                                  ref={fileActionMenuRef}
                                  style={{
                                      top: `${fileActionMenuPosition.top}px`,
                                      left: `${fileActionMenuPosition.left}px`
                                  }}
                                  onClick={event => event.stopPropagation()}
                              >
                                  {!useStackedPreviewLayout && (
                                      <FileActionMenuItem type="button" onClick={openFile}>
                                          <FiExternalLink size={14} />
                                          <span>{lnT('common:open')}</span>
                                      </FileActionMenuItem>
                                  )}
                                  <FileActionMenuItem type="button" onClick={downloadCurrentFile}>
                                      <FiDownload size={14} />
                                      <span>{lnT('messages:download')}</span>
                                  </FileActionMenuItem>
                              </FileActionMenu>,
                              document.body
                          )
                        : null}
                </FileActionsAnchor>
            ) : null}
            {renderCarousel(file)}
        </FileWrapper>
    );
}

const FileWrapper = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    width: 100%;
    min-width: 0;
    position: relative;
`;

const FileContent = styled.div`
    display: flex;
    flex-direction: ${props => props.useStackedPreviewLayout ? 'column' : 'row'};
    width: 100%;
    box-sizing: border-box;
    padding-right: ${props => (props.$hasActions && !props.useStackedPreviewLayout ? '34px' : '0')};
    gap: ${props => props.useStackedPreviewLayout ? '8px' : '0'};
    align-items: ${props => props.useStackedPreviewLayout ? 'stretch' : 'flex-start'};
`;

const ImageContainer = styled.div`
    width: 100%;
    min-width: ${props => (props.$fixedWidth ? `${props.$fixedWidth}px` : '0')};
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${props => (props.$isStarred ? 'rgba(0, 0, 0, 0.06)' : NOTE_BACKGROUND)};
    overflow: hidden;
    max-width: ${props => {
        if (props.isSmallScreen) return '100%';
        if (props.$fixedWidth) return `${props.$fixedWidth}px`;
        return '100%';
    }};
`;

const InfoSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    min-width: 0;
    flex: ${props => props.useStackedPreviewLayout ? 'none' : 1};
    overflow: hidden;
    width: ${props => props.useStackedPreviewLayout ? '100%' : 'auto'};
`;

const FileNameText = styled.div`
    max-width: 100%;
    min-width: 0;
    color: ${props => (props.$isStarred ? 'rgba(35, 39, 42, 0.72)' : WHITE)};
    font-weight: 500;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const FileMetaRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 0;
    width: 100%;
`;

const FileMetaText = styled.span`
    max-width: 100%;
    min-width: 0;
    color: ${props => (props.$isStarred ? PRIMARY_COLOR_4 : PALE_WHITE_4)};
    line-height: 1.35;
    white-space: nowrap;
`;

const FileActionsAnchor = styled.div`
    position: absolute;
    right: ${props => props.$useStackedPreviewLayout ? '6px' : '0'};
    top: ${props => props.$useStackedPreviewLayout ? '6px' : '0'};
    z-index: 3;
`;

const FileActionsButton = styled.button`
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 8px;
    background: ${props => {
        if (props.$isOverlay) {
            return props.$isMenuOpen ? 'rgba(0, 0, 0, 0.72)' : 'rgba(0, 0, 0, 0.48)';
        }
        return 'transparent';
    }};
    backdrop-filter: ${props => props.$isOverlay ? 'blur(6px)' : 'none'};
    -webkit-backdrop-filter: ${props => props.$isOverlay ? 'blur(6px)' : 'none'};
    color: ${props => {
        if (props.$isOverlay) {
            return '#ffffff';
        }
        return props.$isStarred ? PRIMARY_COLOR_8 : 'rgba(255, 255, 255, 0.68)';
    }};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: ${props => {
        if (props.$isOverlay) {
            return props.$isMenuOpen ? 1 : 0;
        }
        return 1;
    }};
    transition: opacity 0.15s ease, background 0.15s ease, color 0.15s ease;

    ${FileWrapper}:hover & {
        opacity: 1;
    }

    &:focus-visible {
        opacity: 1;
        outline: 2px solid ${props => (props.$isOverlay || !props.$isStarred) ? 'rgba(255, 255, 255, 0.72)' : PRIMARY_COLOR_8};
        outline-offset: 2px;
    }

    @media (hover: none) {
        opacity: ${props => props.$isOverlay ? 0.85 : 1};
    }

    &:hover {
        background: ${props => {
            if (props.$isOverlay) {
                return 'rgba(0, 0, 0, 0.78)';
            }
            return props.$isStarred ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)';
        }};
        color: ${props => {
            if (props.$isOverlay) {
                return '#ffffff';
            }
            return props.$isStarred ? PRIMARY_COLOR_8 : 'rgba(255, 255, 255, 0.9)';
        }};
    }
`;

const FileActionMenu = styled.div`
    position: fixed;
    min-width: 154px;
    padding: 4px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(20, 24, 28, 0.96);
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.26);
    backdrop-filter: saturate(180%) blur(18px);
    -webkit-backdrop-filter: saturate(180%) blur(18px);
    z-index: 10010;
`;

const FileNameLink = styled(FileNameText).attrs({ as: 'a' })`
    text-decoration: none;
    cursor: pointer;

    &&,
    &&:link,
    &&:visited,
    &&:active {
        color: ${props => (props.$isStarred ? 'rgba(35, 39, 42, 0.72)' : WHITE)} !important;
    }

    &&:hover {
        color: ${props => (props.$isStarred ? 'rgba(35, 39, 42, 0.82)' : WHITE)} !important;
        text-decoration: underline;
        text-underline-offset: 2px;
        text-decoration-thickness: 1px;
    }
`;

const FileActionMenuItem = styled.button`
    width: 100%;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: rgba(255, 255, 255, 0.86);
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 9px;
    font: inherit;
    font-size: 13px;
    line-height: 1.2;
    text-align: left;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: ${WHITE};
    }
`;

export default FileSection;
